import React, {Suspense} from 'react'
import PropTypes from 'prop-types'
import {Box, Badge, Text, Radio} from '@chakra-ui/react'

import PaymentBankTransfer from './PaymentBankTransfer'
import {toDEformat} from '../../../utils/formatter'
import {BankIcon, CreditCardIcon} from '../../icons'
import Alert from '../Alert'
import CybersourceCreditCardForm from './CybersourceCreditCardForm'

const PaymentOption = ({
  isSelected,
  isDisabled,
  onSelect,
  optionValue,
  logo,
  label,
  badgeText,
  children,
  alertTitle,
  alertMessage,
}) => {
  return (
    <Box py="md" borderBottom="1px solid" borderColor="neutral.glitter">
      <Radio
        name="payment"
        value={optionValue}
        width="100%"
        size="lg"
        isChecked={isSelected}
        onChange={onSelect}
      >
        <Box
          display="flex"
          alignItems="center"
          fontSize="18px"
          fontWeight="medium"
          ml="xs"
        >
          {logo}
          <Text as="span">{label}</Text>
          {badgeText && (
            <Badge
              bg="background.orange"
              borderRadius="md"
              fontWeight="normal"
              px="1"
              mx="md"
              textTransform="none"
            >
              {badgeText}
            </Badge>
          )}
        </Box>
      </Radio>
      {isDisabled && isSelected && alertTitle && alertMessage && (
        <Alert
          status="warning"
          title={alertTitle}
          message={alertMessage}
          ml="md"
          mt="sm"
        />
      )}
      {!isDisabled && isSelected && children}
    </Box>
  )
}

PaymentOption.defaultProps = {
  badgeText: '',
}

PaymentOption.propTypes = {
  isSelected: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  optionValue: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  badgeText: PropTypes.string,
  logo: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  alertTitle: PropTypes.string.isRequired,
  alertMessage: PropTypes.string.isRequired,
}

const PaymentCore = ({
  onSubmitPayment,
  invoice,
  bankAccount,
  paymentOptions,
  authenticityToken,
}) => {
  const [paymentOption, setPaymentOption] = React.useState('')
  const bankPaymentReference = toDEformat(
    `${invoice.supplierCode} ${invoice.number}`,
  )

  const paymentLogo = (paymentType) => {
    if (paymentType === 'bankTransfer') {
      return <BankIcon mr="sm" size="24px" />
    }
    if (paymentType === 'creditCard') {
      return <CreditCardIcon mr="sm" size="24px" />
    }

    return null
  }

  const labels = {
    bankTransfer: 'Bank transfer',
    creditCard: 'Pay by card',
  }

  return (
    <>
      {Object.keys(paymentOptions)
        .filter((paymentType) => paymentOptions[paymentType].visible)
        .map((paymentType) => (
          <PaymentOption
            key={paymentType}
            logo={paymentLogo(paymentType)}
            isSelected={paymentOption === paymentType}
            isDisabled={paymentOptions[paymentType].disabled}
            optionValue={paymentType}
            label={labels[paymentType]}
            onSelect={(e) => setPaymentOption(e.target.value)}
            alertTitle={paymentOptions[paymentType].alertTitle}
            alertMessage={paymentOptions[paymentType].alertMessage}
          >
            {paymentType === 'bankTransfer' && (
              <PaymentBankTransfer
                {...bankAccount}
                paymentReference={bankPaymentReference}
              />
            )}
            {paymentType === 'creditCard' && (
              <Suspense fallback={null}>
                <CybersourceCreditCardForm
                  dueAmount={invoice.dueAmount || '$ N/A'}
                  onSubmitPayment={onSubmitPayment}
                  authenticityToken={authenticityToken}
                  invoiceId={invoice.id}
                />
              </Suspense>
            )}
          </PaymentOption>
        ))}
    </>
  )
}

PaymentCore.defaultProps = {
  onSubmitPayment: () => {},
  paymentOptions: {
    creditCard: true,
    bankTransfer: true,
  },
}

const paymentOptionShape = PropTypes.shape({
  visible: PropTypes.bool,
  disabled: PropTypes.bool,
  alertTitle: PropTypes.string.isRequired,
  alertMessage: PropTypes.string.isRequired,
})

PaymentCore.propTypes = {
  onSubmitPayment: PropTypes.func,
  bankAccount: PropTypes.shape({
    accountName: PropTypes.string.isRequired,
    accountNumber: PropTypes.string.isRequired,
    bsb: PropTypes.string.isRequired,
    payid: PropTypes.string,
  }).isRequired,
  invoice: PropTypes.shape({
    id: PropTypes.string,
    supplierName: PropTypes.string,
    supplierCode: PropTypes.string,
    number: PropTypes.string,
    amount: PropTypes.string,
    dueAmount: PropTypes.string,
  }).isRequired,
  paymentOptions: PropTypes.shape({
    creditCard: paymentOptionShape,
    bankTransfer: paymentOptionShape,
  }),
  authenticityToken: PropTypes.string.isRequired,
}

export default PaymentCore
